<template>
    <b-card
        no-body
        border-variant="light"
    >
        <div class="card-header">
            <div class="col-md-6 pl-0">
                <b-card-title>{{ $t('Page(SendRequest):Component(Label):request-details') }}</b-card-title>
            </div>
        </div>
        <div class="pl-2 pr-2 pb-2">
            <div class="row pb-1">
                <div class="col-md-4 col-lg-4">
                    <h6>{{ $t('Page(SendRequest):Component(Label):requested-legislations') }}</h6>
                    <b-list-group>
                        <b-list-group-item button v-for="legislation in legislationsToForward">
                            <b-form-checkbox
                                :value="true"
                                v-model="legislation.selected"
                                @change="onLegislationChange(legislation)"
                            >
                                {{ legislation.title }}
                            </b-form-checkbox>
                        </b-list-group-item>
                    </b-list-group>
                </div>
                <div class="col-md-4 col-lg-4">
                    <h6>{{ $t('Page(SendRequest):Component(Label):verification-documents') }}</h6>
                    <b-list-group>
                        <b-list-group-item v-for="verificationDocument in verificationDocuments">
                            <b-form-checkbox
                                :value="true"
                                :disabled="verificationDocument.disabled"
                                v-model="verificationDocument.selected"
                            >
                                {{ $t('Component(VerificationDocumentTypeEnum):' + verificationDocument.type) }}
                            </b-form-checkbox>
                            <div
                                v-if="verificationDocument.selected && 'evidence' === verificationDocument.type"
                                class="pt-1 evidence-types"
                            >
                                <b-form-group
                                    :label="$t('Component(Verification):type-of-document')">
                                    <v-select
                                        multiple
                                        v-model="selectedEvidenceReportTypes"
                                        :placeholder="$t('Component(Composition):placeholder-document-types')"
                                        :options="evidenceReportTypes"
                                        :getOptionLabel="(o) => o.title"
                                    />
                                </b-form-group>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </div>
                <div class="col-md-4 col-lg-4">
                    <div
                        v-if="showLevelOfInformation"
                        class="pb-1"
                    >
                        <h6 class="pb-0 mb-0">{{ $t('Page(SendRequest):Component(Label):level-information') }}</h6>
                        <div class="pt-0 mt-0 demo-inline-spacing">
                            <b-form-radio
                                v-model="informationLevel"
                                name="some-radio9"
                                value="concern_substances"
                                class="custom-control-primary"
                            >
                                {{ $t('Page(SendRequest):Component(Placeholder):information-on-concern_substances') }}
                            </b-form-radio>
                            <b-form-radio
                                v-model="informationLevel"
                                name="some-radio9"
                                value="fmd"
                                class="custom-control-primary"
                            >
                                {{ $t('Page(SendRequest):Component(Placeholder):fmd') }}
                            </b-form-radio>
                        </div>
                    </div>
                    <div>
                        <h6>{{ $t('Page(SendRequest):Component(Label):last-response-date') }}</h6>
                        <b-form-datepicker
                            v-model="responseLastDate"
                            :min="minDate"
                            :date-format-options="$store.state.component.datePicker.format"
                            :locale="$store.state.component.datePicker.locale"
                        />
                    </div>
                </div>
            </div>
            <div class="row pt-2">
                <div class="col-md-12 col-lg-12">
                    <h6>{{ $t('Page(SendRequest):Component(Label):additional-message') }}</h6>
                    <b-form-textarea
                        id="textarea-auto-height"
                        :placeholder="$t('Page(SendRequest):Component(Placeholder):additional-message')"
                        rows="3"
                        max-rows="6"
                        v-model="additionalMessage"
                    />
                </div>
            </div>
        </div>
    </b-card>
</template>

<script>
import { BFormRadio, BCardTitle, BCard, BFormDatepicker, BFormGroup,
    BListGroup, BListGroupItem, BFormCheckbox, BFormTextarea } from 'bootstrap-vue'
import {VerificationDocumentType} from "@/libs/product";
import {LegislationApi} from "@core/api";
import vSelect from 'vue-select'
import {LegislationKey, RegulationType} from "@core/domain/legislation";
import {isChemity, isSustainity} from "@/libs/application";

export default {
    components: {
        BFormTextarea, BListGroup, BListGroupItem, BFormDatepicker, BFormRadio, BCard, vSelect, BCardTitle,
        BFormCheckbox, BFormGroup,
    },
    props: {
        request: {
            type: Object,
            default() {
                return {
                    id: null,
                    legislations: [],
                    verificationDocuments: [],
                };
            }
        },
        hiddenLegislations: {
            type: Array,
            default() {
                return [];
            }
        },
        initiallyUnselectedLegislations: {
            type: Array,
            default() {
                return [];
            }
        },
        lastResponseDate: {
            default() {
                let date = new Date();
                date.setDate(date.getDate() + 45);
                return date;
            }
        }
    },
    data() {
        let date = new Date();
        date.setDate(date.getDate() + 45);
        return {
            verificationDocuments: VerificationDocumentType.all().map(type => {
                return {
                    type: type,
                    selected: this.request.verificationDocuments.findIndex(d => type === d) !== -1,
                    disabled: false
                };
            }),
            minDate: new Date(),
            responseLastDate: this.lastResponseDate ?? date,
            informationLevel: 'concern_substances',
            additionalMessage: '',
            legislations: [],
            evidenceReportTypes: [],
            selectedEvidenceReportTypes: [],
            legislationsToForward: []
        };
    },
    methods: {
        getData() {
            return {
                legislations: this.legislationsToForward.filter(l => l.selected).map(l => l.id),
                verificationDocuments: this.verificationDocuments.filter(d => d.selected).map(d => d.type),
                informationLevel: this.informationLevel,
                responseLastDate: this.responseLastDate,
                message: this.additionalMessage,
                evidenceReportTypes: this.selectedEvidenceReportTypes.map(t => t.id)
            };
        },
        onLegislationChange(legislation) {
            this.adjustVerificationDocuments();
            this.manageRequiredVerificationDocuments();
        },
        adjustVerificationDocuments() {
            if (isSustainity()) {
                return;
            }
            const selectedLegislations = this.legislationsToForward.filter(l => l.selected)

            if (selectedLegislations.length > 0 && selectedLegislations.every(l => !Array.isArray(l.verificationDocumentTypes))) {
                return
            }

            const availableTypes = []
            selectedLegislations
                .forEach(l => {
                    (l.verificationDocumentTypes || []).forEach(type => {
                        if (!availableTypes.includes(type)) {
                            availableTypes.push(type);
                        }
                    })
                });

            this.verificationDocuments.forEach(d => {
                if (availableTypes.includes(d.type)) {
                    d.disabled = false;
                } else {
                    d.disabled = true;
                    if (d.selected) {
                        d.selected = false;
                    }
                }
            });
        },
        manageRequiredVerificationDocuments() {
            const selectedLegislations = this.legislationsToForward.filter(l => l.selected)

            if (selectedLegislations.length > 0 && selectedLegislations.every(l => !Array.isArray(l.mandatoryVerificationDocumentTypes))) {
                return
            }

            const selectedTypes = []
            selectedLegislations
                .forEach(l => {
                    (l.mandatoryVerificationDocumentTypes || []).forEach(type => {
                        if (!selectedTypes.includes(type)) {
                            selectedTypes.push(type);
                        }
                    });
                });

            this.verificationDocuments.forEach(d => {
                if (!d.disabled && !d.selected && selectedTypes.includes(d.type)) {
                    d.selected = true;
                } else if (!d.disabled && d.selected && !selectedTypes.includes(d.type)) {
                    d.selected = false;
                }
            });
        },
        applyInitialLegislationSelection() {
            const initiallyUnselected = new Set(
                this.initiallyUnselectedLegislations.map(id => String(id)),
            );

            this.legislations.forEach(legislation => {
                legislation.selected = !initiallyUnselected.has(String(legislation.id));
            });
        },
        prepareLegislationsToForward() {
            if (this.request.id === null) {
                this.legislationsToForward = this.legislations;
                return;
            }
            this.legislationsToForward = (this.request.legislations || [])
                .filter(l => !this.hiddenLegislations.includes(l.id))
                .map(l => {
                    const legislation = this.legislations.find(ll => ll.id === l.id)

                    return {
                        ...l,
                        ...(legislation || {}),
                        selected: true,
                    }
                })
        }
    },
    computed: {
        showLevelOfInformation() {
            return isChemity();
        }
    },
    watch: {
        request(request) {
            this.legislations.forEach(l => l.selected = false);
            request.legislations.forEach(l => {
                let legislation = this.legislations.find(ll => ll.id === l.id);

                if (legislation) {
                    legislation.selected = true;
                }
            })

            request.verificationDocuments.forEach(d => {
                const doc = this.verificationDocuments.find(vd => vd.type === d);

                if (doc) {
                    doc.selected = true;
                }
            });

            this.prepareLegislationsToForward();
            this.informationLevel = request.informationLevel;
        },
        hiddenLegislations() {
            this.legislations = this.legislations.filter(l => !this.hiddenLegislations.includes(l.id));
            this.prepareLegislationsToForward()
        },
        initiallyUnselectedLegislations() {
            this.applyInitialLegislationSelection();
            this.prepareLegislationsToForward();
            this.adjustVerificationDocuments();
            this.manageRequiredVerificationDocuments();
        },
        lastResponseDate() {
            this.responseLastDate = this.lastResponseDate;
        }
    },
    created() {
        LegislationApi.all().then(r => {
            const includeByApplication = l => {
                const isClimateDirective = l.regulationType === RegulationType.CLIMATE_DIRECTIVE;
                return isChemity() ? !isClimateDirective : isClimateDirective;
            };

            this.legislations = r.data
                .filter(includeByApplication)
                .filter(l => !this.hiddenLegislations.includes(l.id));
            this.applyInitialLegislationSelection();

            this.prepareLegislationsToForward();
            this.adjustVerificationDocuments();
            this.manageRequiredVerificationDocuments();
        });
        LegislationApi.getEvidenceReportTypes().then(r => this.evidenceReportTypes = r.data);
    }
}
</script>
